import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map as rxMap, shareReplay, take } from 'rxjs/operators';
import { IProfileHeaderStudentSearchStudentsDetails } from 'Src/ng2/shelter/profile/profile-header/profile-header.models';
import { ISingleStudent } from 'Src/ng2/student/student-data-service/single-student-data.interface';
import { StudentProfileData } from 'Src/ng2/student/student-data-service/student-data.service';
import { IAgGridRequest } from '../../typings/interfaces/ag-grid-request.interface';
import { TValidPartnerTypes } from '../../typings/interfaces/partner.interface';
import { ENTITY_TYPE, TOpEntityType } from '../../typings/types/entity-type.types';
import { TEntityMode } from '../../typings/types/entity-mode.types';
import { TDistrictId } from '../../typings/interfaces/district.interface';

export type TSearchOptions = IProfileHeaderStudentSearchStudentsDetails[];

export interface IGlobalSearchContext {
  entityMode: TEntityMode;
  entityId?: string | null; // if null, it means user is on multi entity view, otherwise user is on single entity view
  entityType: TOpEntityType | null;
  networkId?: string | null;
  districtId?: TDistrictId | null; // if null, it means user is on non-district specific view, otherwise user is on district specific view
  subEntityId?: string | null;
}

/**
 * Sources the student roster the global search box filters against.
 *
 * Held here, not on the component: every entity route declares `component: LayoutSwitcherComponent`,
 * so a sibling navigation rebuilds the shell and a component-held stream would refetch each time.
 *
 * Never invalidated — no in-app action changes a roster, so a sync landing mid-session is picked up
 * on the next full load.
 */
@Injectable({ providedIn: 'root' })
export class GlobalSearchDataService {
  private studentSearchOptions$: Observable<TSearchOptions>;
  private contextKey: string;

  constructor (private studentDataService: StudentProfileData) {}

  public getStudentSearchOptions$ ({ context }: { context: IGlobalSearchContext }): Observable<TSearchOptions> {
    const contextKey = this.getContextKey(context);
    if (this.studentSearchOptions$ && this.contextKey === contextKey) {
      return this.studentSearchOptions$;
    }

    this.contextKey = contextKey;
    this.studentSearchOptions$ = this.studentDataService
      .getGlobalSearchStudents$({
        contextPartnerId: context.entityId,
        contextPartnerType: context.entityType as any as TValidPartnerTypes,
        request: this.buildAllStudentsRequest(),
      })
      .pipe(
        take(1),
        rxMap(({ rowData }) => this.toSearchOptions(rowData, context.entityType)),
        shareReplay(1),
      );

    return this.studentSearchOptions$;
  }

  private getContextKey (context: IGlobalSearchContext): string {
    let resourceId = context?.entityId || context?.networkId || 'NO_RESOURCE_ID';
    if (context?.subEntityId) resourceId = resourceId.concat(`::${context.subEntityId}`);
    return `${context?.entityMode}::${context?.entityType}::${resourceId}::${context?.districtId || 'NO_DISTRICT_ID'}`;
  }

  // Unpaged and unsorted: the search box filters client side.
  private buildAllStudentsRequest (): IAgGridRequest {
    return {
      endRow: null,
      filterModel: null,
      sortModel: [],
      startRow: null,
      valueCols: '',
    };
  }

  // Shelter rosters arrive on their own columns.
  private toSearchOptions (rowData: ISingleStudent[], entityType: TOpEntityType): TSearchOptions {
    const isShelter = entityType === ENTITY_TYPE.SHELTER;

    return rowData.map((student: ISingleStudent) => ({
      grade: student.SHELTER_GRADE || student.GRADE,
      _id: isShelter ? student.CARES_ID : student._id,
      studentId: student.SHELTER_OSIS_NUMBER || student.OSIS_NUMBER,
      studentName: isShelter ? student.SHELTER_STUDENT_NAME : student.STUDENT_NAME,
    }));
  }
}
